import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import { apiService } from "../../services/api.service";
import { helperService } from "../../services/helper.service";
import { toast } from "../../components/Toast/ToastManager";
import { Constants, DOG_OWNER_PREMIUM_ENABLED } from "@eagerdog/constants";
import { IUser } from "@eagerdog/interfaces";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import EventDetailsHeader from "./components/EventDetailsHeader";
import EventMetrics from "./components/EventMetrics";
import ShowAwardsSection from "./components/ShowAwardsSection";
import EntriesBreakdownTable from "./components/EntriesBreakdownTable";
import DetailedResultsTable from "./components/DetailedResultsTable";
import styles from "./EventSummary.module.scss";

interface IEventMetrics {
  dogs: number;
  entries: number;
  distinctBreeds: number;
}

const EventSummary: React.FC = () => {
  const { eid } = useParams<{ eid: string }>();
  const navigate = useNavigate();
  const user: IUser = helperService.getUser();

  const [event, setEvent] = useState<any>(null);
  const [shows, setShows] = useState<any[]>([]);
  const [metrics, setMetrics] = useState<IEventMetrics | null>(null);
  const [scores, setScores] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Removed automatic scroll-to-top on mount
  // Browser handles scroll restoration for navigation
  // EventSummary is accessed via direct navigation, so browser scrolls to top naturally

  // Access control check
  useEffect(() => {
    if (event && user) {
      const isPastEvent = moment().isAfter(moment(event.end_date).endOf('day'));
      
      // Past results are free for everyone while Dog Owner Premium is retired.
      if (DOG_OWNER_PREMIUM_ENABLED && isPastEvent && user.user_type === Constants.user_type.owner && !user.has_past_results_access) {
        toast.show({
          title: "Access Restricted",
          content: "You don't have permission to view past event results. Upgrade your account to access this feature.",
          duration: 8000,
          type: "error",
        });
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    }
  }, [event, user, navigate]);

  // Load event data
  useEffect(() => {
    if (eid) {
      setLoading(true);
      
      // First get the event to get the ID
      apiService.getEventSummaryForHandle(eid)
        .then((eventData: any) => {
          setEvent(eventData);
          
          // Fetch shows separately
          return Promise.all([
            apiService.getClubEventShows(eventData.club_id, eventData._id),
            apiService.getEventMetricsPublic(eventData._id)
          ]);
        })
        .then(([showsData, metricsData]: [any[], any]) => {
          setShows(showsData);
          setMetrics(metricsData);
        })
        .catch((error: any) => {
          console.error('Failed to load event:', error);
          if (error.response && error.response.status === 403) {
            // Permission error already handled by access control check
            return;
          }
          toast.show({
            title: "Error",
            content: "Failed to load event summary",
            duration: 5000,
            type: "error",
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [eid]);

  // Load scores separately (larger dataset)
  useEffect(() => {
    if (event && event._id) {
      apiService.getEventScoresReadOnly(event._id, {
        sort: [{ attribute_name: "show_type", sort: "asc" }]
      })
        .then(setScores)
        .catch((error) => {
          if (error.response && error.response.status === 403) {
            // Permission error - silently handle (parent handles toast)
            console.warn('Access denied to scores, handled by parent component');
          } else {
            console.error('Failed to load scores:', error);
          }
        });
    }
  }, [event]);

  if (loading) {
    return (
      <div className={styles.EventSummary}>
        <div className={styles.container}>
          <div className={styles.loading}>Loading event summary...</div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className={styles.EventSummary}>
        <div className={styles.container}>
          <div className={styles.error}>Event not found</div>
        </div>
      </div>
    );
  }

  const breadcrumbs = [
    { label: "Events", path: "/" },
    { label: `${event.title} Summary`, path: "" }
  ];

  return (
    <div className={styles.EventSummary}>
      <div className={styles.container}>
        <Breadcrumb crumbs={breadcrumbs} />
        
        <div className={styles.eventSummarySection}>
          <div className={styles.infoBox}>
            This event has ended. Below is a complete summary and results
          </div>
          
          <EventDetailsHeader event={event} />
        </div>
        
        {metrics && (
          <EventMetrics 
            dogs={metrics.dogs}
            entries={metrics.entries}
            distinctBreeds={metrics.distinctBreeds}
          />
        )}
        
        {shows.length > 0 && scores.length > 0 && (
          <ShowAwardsSection event={event} shows={shows} scores={scores} />
        )}
        
        {scores.length > 0 && (
          <>
            <EntriesBreakdownTable scores={scores} event={event} />
            <DetailedResultsTable scores={scores} event={event} shows={shows} />
          </>
        )}
        
        {scores.length === 0 && !loading && (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            No results available for this event yet.
          </div>
        )}
      </div>
    </div>
  );
};

export default EventSummary;

